import React, { useState } from 'react';
import {Dialog, Portal, Button, Text, VStack, Box, HStack, Icon, Separator, Alert, RadioGroup, Badge, Skeleton} from "@chakra-ui/react";
import { toaster } from "../ui/toaster";
import {useColorModeValue} from "../ui/color-mode";
import { LuCheck, LuAlertTriangle } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { SubscriptionStatusResponse, BillingInterval } from '../../types';
import {
    useCreateCheckoutSessionMutation,
    useDowngradeToFreeMutation,
    useGetPricingQuery} from '../../services/subscriptionService';
import { analytics, getCheckoutAttribution } from '../../utils/analytics';
import { useDialogBodyCleanup } from '../../hooks/useDialogBodyCleanup';

interface PaymentRequiredModalProps {
    subscription: SubscriptionStatusResponse;
    open: boolean;
}

type Step = 'plan' | 'billing' | 'free-confirm';

const PLAN_FEATURES: Record<string, string[]> = {
    starter: [
        'subscription.plan_features.starter.online_booking',
        'subscription.plan_features.starter.staff_2',
        'subscription.plan_features.starter.email_notifications',
        'subscription.plan_features.starter.basic_calendar',
    ],
    standard: [
        'subscription.plan_features.standard.everything_starter',
        'subscription.plan_features.standard.staff_5',
        'subscription.plan_features.standard.sms_100',
        'subscription.plan_features.standard.analytics',
        'subscription.plan_features.standard.google_calendar',
    ],
    pro: [
        'subscription.plan_features.pro.everything_standard',
        'subscription.plan_features.pro.unlimited_staff',
        'subscription.plan_features.pro.sms_500',
        'subscription.plan_features.pro.priority_explore',
        'subscription.plan_features.pro.loyalty_stamps',
    ]};

export const PaymentRequiredModal: React.FC<PaymentRequiredModalProps> = ({
    subscription,
    open}) => {
    const { t } = useTranslation();
        const [step, setStep] = useState<Step>('plan');
    const [selectedPlan, setSelectedPlan] = useState<string>(
        subscription.planType && subscription.planType !== 'free'
            ? subscription.planType
            : 'starter'
    );
    const [billingInterval, setBillingInterval] = useState<BillingInterval>('monthly');
    const [createCheckout, { isLoading: isCheckoutLoading }] = useCreateCheckoutSessionMutation();
    const [downgradeToFree, { isLoading: isDowngradeLoading }] = useDowngradeToFreeMutation();
    const { data: pricing, isLoading: isPricingLoading } = useGetPricingQuery();
    useDialogBodyCleanup(open)

    const bgColor = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const selectedBorderColor = useColorModeValue('blue.500', 'blue.400');
    const hoverBgColor = useColorModeValue('gray.50', 'gray.700');

    const getPlanPricing = (planType: string) => {
        if (!pricing || planType === 'free') return null;
        return pricing[planType as keyof typeof pricing];
    };

    const selectedPlanPricing = getPlanPricing(selectedPlan);

    const handleContinueWithPlan = async () => {
        analytics.checkoutStarted(selectedPlan, billingInterval);
        try {
            const result = await createCheckout({
                planType: selectedPlan,
                billingInterval,
                successUrl: `${window.location.origin}/dashboard?subscription=success&plan=${selectedPlan}&interval=${billingInterval}`,
                cancelUrl: `${window.location.origin}/dashboard?subscription=canceled&plan=${selectedPlan}`,
                ...getCheckoutAttribution()}).unwrap();

            window.location.href = result.checkoutUrl;
        } catch (error) {
            analytics.checkoutFailed(selectedPlan, 'session_creation_failed');
            toaster.create({
                title: t('subscription.error.checkout_failed'),
                type: 'error',
                duration: 5000,
                closable: true});
        }
    };

    const handleDowngradeToFree = async () => {
        try {
            await downgradeToFree().unwrap();
            analytics.subscriptionDowngraded(subscription?.planType ?? 'unknown', 'free');
            toaster.create({
                title: t('subscription.success.downgraded'),
                type: 'success',
                duration: 3000,
                closable: true});
            setStep('plan');
            window.location.reload();
        } catch (error) {
            toaster.create({
                title: t('subscription.error.downgrade_failed'),
                type: 'error',
                duration: 5000,
                closable: true});
        }
    };

    const handlePlanSelect = (plan: string) => {
        setSelectedPlan(plan);
        setStep('billing');
    };

    if (!open) return null;

    const renderPlanSelection = () => (
        <VStack gap={4} align="stretch">
            <Alert.Root status="warning" borderRadius="md">
                <Alert.Indicator />
                <Text fontSize="sm">
                    {t('subscription.payment_required.description')}
                </Text>
            </Alert.Root>

            <Text fontWeight="bold" fontSize="lg">
                {t('subscription.select_plan.title')}
            </Text>

            {isPricingLoading ? (
                <VStack gap={3}>
                    <Skeleton height="120px" width="100%" borderRadius="md" />
                    <Skeleton height="120px" width="100%" borderRadius="md" />
                    <Skeleton height="120px" width="100%" borderRadius="md" />
                </VStack>
            ) : (
                <VStack gap={3} align="stretch">
                    {(['starter', 'standard', 'pro'] as const).map((plan) => {
                        const planPricing = getPlanPricing(plan);
                        const isCurrentPlan = subscription.planType === plan;
                        const isRecommended = plan === 'standard';

                        return (
                            <Box
                                key={plan}
                                as="button"
                                onClick={() => handlePlanSelect(plan)}
                                borderWidth="2px"
                                borderColor={selectedPlan === plan ? selectedBorderColor : borderColor}
                                borderRadius="lg"
                                p={4}
                                textAlign="left"
                                _hover={{ bg: hoverBgColor, borderColor: selectedBorderColor }}
                                transition="all 0.2s"
                                position="relative"
                            >
                                {isRecommended && (
                                    <Badge
                                        colorPalette="blue"
                                        position="absolute"
                                        top="-10px"
                                        right="10px"
                                        fontSize="xs"
                                    >
                                        {t('subscription.recommended')}
                                    </Badge>
                                )}
                                {isCurrentPlan && (
                                    <Badge
                                        colorPalette="green"
                                        position="absolute"
                                        top="-10px"
                                        left="10px"
                                        fontSize="xs"
                                    >
                                        {t('subscription.current_plan')}
                                    </Badge>
                                )}
                                <HStack justify="space-between" align="start">
                                    <VStack align="start" gap={1}>
                                        <Text fontWeight="bold" fontSize="lg">
                                            {t(`subscriptions.accountTypes.${plan}`)}
                                        </Text>
                                        <Text fontSize="sm" color="gray.500">
                                            {t(`subscription.plan_descriptions.${plan}`)}
                                        </Text>
                                    </VStack>
                                    {planPricing && (
                                        <VStack align="end" gap={0}>
                                            <Text fontWeight="bold" fontSize="xl">
                                                €{planPricing.monthly.price}
                                            </Text>
                                            <Text fontSize="xs" color="gray.500">
                                                {t('subscription.per_month')}
                                            </Text>
                                        </VStack>
                                    )}
                                </HStack>
                                <VStack as="ul" mt={2} gap={1} align="stretch" listStyleType="none">
                                    {PLAN_FEATURES[plan].slice(0, 3).map((feature, idx) => (
                                        <HStack as="li" key={idx} fontSize="sm" gap={2}>
                                            <Icon as={LuCheck} color="green.500" />
                                            <Text fontSize="sm">{t(feature)}</Text>
                                        </HStack>
                                    ))}
                                </VStack>
                            </Box>
                        );
                    })}
                </VStack>
            )}

            {subscription.canDowngradeToFree && (
                <>
                    <Separator />
                    <Box
                        borderWidth="1px"
                        borderColor={borderColor}
                        borderRadius="lg"
                        p={4}
                    >
                        <Text fontWeight="bold" mb={2}>
                            {t('subscription.free_tier_option')}
                        </Text>
                        <Text fontSize="sm" color="gray.600" mb={3}>
                            {t('subscription.free_tier_description')}
                        </Text>
                        <Button
                            variant="outline"
                            width="100%"
                            onClick={() => setStep('free-confirm')}
                        >
                            {t('subscription.downgrade_to_free')}
                        </Button>
                    </Box>
                </>
            )}
        </VStack>
    );

    const renderBillingSelection = () => (
        <VStack gap={4} align="stretch">
            <HStack>
                <Button variant="ghost" size="sm" onClick={() => setStep('plan')}>
                    {t('common.back')}
                </Button>
                <Text fontWeight="bold" fontSize="lg">
                    {t('subscription.continue_with_plan', {
                        plan: t(`subscriptions.accountTypes.${selectedPlan}`)
                    })}
                </Text>
            </HStack>

            <Text fontSize="sm" color="gray.600">
                {t('subscription.billing_interval.title')}
            </Text>

            {isPricingLoading ? (
                <VStack gap={3}>
                    <Skeleton height="70px" width="100%" borderRadius="md" />
                    <Skeleton height="70px" width="100%" borderRadius="md" />
                </VStack>
            ) : selectedPlanPricing ? (
                <RadioGroup.Root value={billingInterval} onValueChange={(e) => setBillingInterval(e.value as BillingInterval)}>
                    <VStack gap={4} align="stretch">
                        <Box
                            as="label"
                            cursor="pointer"
                            borderWidth="2px"
                            borderColor={billingInterval === 'monthly' ? selectedBorderColor : borderColor}
                            borderRadius="md"
                            p={3}
                            _hover={{ borderColor: selectedBorderColor }}
                            transition="border-color 0.2s"
                        >
                            <HStack justify="space-between">
                                <HStack>
                                    <RadioGroup.Item value="monthly">
                                        <RadioGroup.ItemHiddenInput />
                                        <RadioGroup.ItemIndicator />
                                    </RadioGroup.Item>
                                    <VStack align="start" gap={0}>
                                        <Text fontWeight="medium">
                                            {t('subscription.billing_interval.monthly')}
                                        </Text>
                                        <Text fontSize="sm" color="gray.500">
                                            {t('subscription.billing_interval.billed_monthly')}
                                        </Text>
                                    </VStack>
                                </HStack>
                                <Text fontWeight="bold" fontSize="lg">
                                    €{selectedPlanPricing.monthly.price}
                                </Text>
                            </HStack>
                        </Box>

                        <Box
                            as="label"
                            cursor="pointer"
                            borderWidth="2px"
                            borderColor={billingInterval === 'yearly' ? selectedBorderColor : borderColor}
                            borderRadius="md"
                            p={3}
                            pt={4}
                            _hover={{ borderColor: selectedBorderColor }}
                            transition="border-color 0.2s"
                            position="relative"
                        >
                            <Badge
                                colorPalette="green"
                                position="absolute"
                                top="-12px"
                                right="10px"
                                fontSize="xs"
                                px={2}
                                py={1}
                            >
                                {t('subscription.billing_interval.save', { amount: selectedPlanPricing.yearly.savings })}
                            </Badge>
                            <HStack justify="space-between">
                                <HStack>
                                    <RadioGroup.Item value="yearly">
                                        <RadioGroup.ItemHiddenInput />
                                        <RadioGroup.ItemIndicator />
                                    </RadioGroup.Item>
                                    <VStack align="start" gap={0}>
                                        <Text fontWeight="medium">
                                            {t('subscription.billing_interval.yearly')}
                                        </Text>
                                        <Text fontSize="sm" color="gray.500">
                                            {t('subscription.billing_interval.monthly_equivalent', { amount: selectedPlanPricing.yearly.monthly_equivalent })}
                                        </Text>
                                    </VStack>
                                </HStack>
                                <Text fontWeight="bold" fontSize="lg">
                                    €{selectedPlanPricing.yearly.price}
                                </Text>
                            </HStack>
                        </Box>
                    </VStack>
                </RadioGroup.Root>
            ) : null}

            <Button
                colorPalette="blue"
                size="lg"
                width="100%"
                onClick={handleContinueWithPlan}
                loading={isCheckoutLoading}
            >
                {t('subscription.activate_subscription')}
            </Button>
        </VStack>
    );

    const renderFreeConfirm = () => (
        <VStack gap={4} align="stretch">
            <Alert.Root status="info" borderRadius="md">
                <Alert.Indicator />
                <Text fontSize="sm">
                    {t('subscription.free_tier_confirm.description')}
                </Text>
            </Alert.Root>

            <Box>
                <Text fontWeight="bold" mb={2}>
                    {t('subscription.free_tier_confirm.limitations')}
                </Text>
                <VStack as="ul" gap={2} align="stretch" listStyleType="none">
                    <HStack as="li" fontSize="sm" gap={2}>
                        <Icon as={LuAlertTriangle} color="orange.500" />
                        <Text fontSize="sm">{t('subscription.free_tier_confirm.limit_bookings')}</Text>
                    </HStack>
                    <HStack as="li" fontSize="sm" gap={2}>
                        <Icon as={LuAlertTriangle} color="orange.500" />
                        <Text fontSize="sm">{t('subscription.free_tier_confirm.no_sms')}</Text>
                    </HStack>
                    <HStack as="li" fontSize="sm" gap={2}>
                        <Icon as={LuAlertTriangle} color="orange.500" />
                        <Text fontSize="sm">{t('subscription.free_tier_confirm.basic_analytics')}</Text>
                    </HStack>
                    <HStack as="li" fontSize="sm" gap={2}>
                        <Icon as={LuAlertTriangle} color="orange.500" />
                        <Text fontSize="sm">{t('subscription.free_tier_confirm.branding')}</Text>
                    </HStack>
                </VStack>
            </Box>

            <HStack gap={3}>
                <Button
                    variant="outline"
                    flex={1}
                    onClick={() => setStep('plan')}
                >
                    {t('common.back')}
                </Button>
                <Button
                    colorPalette="orange"
                    flex={1}
                    onClick={handleDowngradeToFree}
                    loading={isDowngradeLoading}
                >
                    {t('subscription.confirm_downgrade')}
                </Button>
            </HStack>
        </VStack>
    );

    return (
        <Dialog.Root open={open} onOpenChange={() => {}} closeOnInteractOutside={false} size="lg" placement="center">
            <Portal>
                <Dialog.Backdrop bg="blackAlpha.700" backdropFilter="blur(4px)" />
                <Dialog.Positioner>
                    <Dialog.Content bg={bgColor} mx={4}>
                        <Dialog.Header textAlign="center" pb={2}>
                            <VStack gap={2}>
                                <Icon as={LuAlertTriangle} color="orange.500" boxSize={8} />
                                <Dialog.Title fontSize="xl">{t('subscription.payment_required.title')}</Dialog.Title>
                            </VStack>
                        </Dialog.Header>

                        <Dialog.Body>
                            {step === 'plan' && renderPlanSelection()}
                            {step === 'billing' && renderBillingSelection()}
                            {step === 'free-confirm' && renderFreeConfirm()}
                        </Dialog.Body>

                        <Dialog.Footer justifyContent="center">
                            <Text fontSize="xs" color="gray.500" textAlign="center">
                                {t('subscription.payment_required.contact_support')}
                            </Text>
                        </Dialog.Footer>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
};
